import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { alpha } from '@mui/material'
import { type ChartDataset } from 'chart.js'
import { KNCustomsIcon } from 'components/KNIcon/KNMaterialIcon'
import { parseISO, differenceInSeconds, subSeconds } from 'date-fns'
import i18n from 'i18next'

// @mui imports
import theme from 'assets/theme'

// KN imports
import { type Annotation, getDatasetColors } from 'global/helpers/charts'
import { getDistance, getHeading } from 'components/KNMapLibre/helpers'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import {
  type LegData,
  type StopData,
  type StopsSequenceGroup,
  type GeoPoint,
  type GeoPointsGroup,
  type VehicleTemperatureMetricValue,
  type VehiclePositionMetricValue,
  type PharmaExtendedInformation,
  type StopStatus,
  ParkingLocationType, type StopLegPair,
} from './TripDetails.types'

export const isStopsViewAvailable = (trip: TripData): boolean => {
  if (trip.sendingApplication === 'MACADAM') {
    return false
  }
  const countryCode = trip.logicalSender.substring(0, 2)
  const unsupportedRegions = [
    'GB',
    'FR',
  ]
  return !unsupportedRegions.includes(countryCode)
}

export const groupStopsBySequence = (legs: LegData[]): StopsSequenceGroup[] =>
  legs
    .reduce((groups: StopsSequenceGroup[], leg) => {
      // create groups with stops and its related legs based on stop's sequence
      leg.wayPoints.forEach((stop) => {
        groups[stop.sequence] ??= {
          id: stop.sequence,
          stopLegPairs: [],
          state: 'pending',
          pickupsCount: 0,
          deliveriesCount: 0,
          customsCount: 0,
        }
        groups[stop.sequence]!.stopLegPairs.push({
          stop,
          leg,
        })
        if (stop.type === 'PUP') {
          groups[stop.sequence]!.pickupsCount += 1
        } else if (stop.type === 'DEL') {
          groups[stop.sequence]!.deliveriesCount += 1
        } else if (stop.type === 'CUS') {
          groups[stop.sequence]!.customsCount += 1
        }
      })
      return groups
    }, [])
    // ensure that groups are sorted by sequence
    .sort((a, b) => a.id - b.id)
    .map((group) => {
      // sort stops within groups by shipment number
      group.stopLegPairs = [...group.stopLegPairs].sort((a, b) => {
        if (a.leg.shipmentNumber === b.leg.shipmentNumber) {
          return 0
        }
        return a.leg.shipmentNumber < b.leg.shipmentNumber ? -1 : 1
      })
      // calculate state of all groups
      const stops = group.stopLegPairs.map((stopLegPair) => stopLegPair.stop)
      const inProgressStops = stops.filter(
        (stop) => stop.statuses.length > 0 || (stop.documents && stop.documents.length > 0),
      )
      const completedStops = stops.filter((stop) => stop.availableStatuses.length === 0)
      if (stops.length === completedStops.length) {
        group.state = 'completed'
      } else if (inProgressStops.length > 0) {
        group.state = 'in_progress'
      }
      return group
    })
    // fix sparse array length
    .filter((group) => true)

export const getStopsGroupStateColor = (state: string): string => {
  switch (state) {
    case 'completed':
      return theme.palette.success.main
    case 'in_progress':
      return theme.palette.primary.main
    case 'pending':
      return theme.palette.secondary.main
    default:
      return theme.palette.primary.light
  }
}

export const temperatureDataTransformer = (data: VehicleTemperatureMetricValue[], earliestPickupTime: number | null, latestDeliveryTime: number | null): ChartDataset<'line'>[] => {
  const colors = getDatasetColors()
  return Object.values(
    data.reduce((datasets: Record<string, ChartDataset<'line'>>, point) => {
      const datasetName = point.sensorId ?? 'dataset'
      if (!datasets[datasetName]) {
        const nextColor: string = colors.pop() ?? theme.palette.common.white
        const fadedColor: string = nextColor.toString() + '33'

        datasets[datasetName] = {
          label: datasetName,
          data: [],
          borderColor: nextColor,
          segment: {
            borderColor: (ctx): string => {
              const value = ctx.p0.parsed.x
              if (earliestPickupTime !== null && value < earliestPickupTime) {
                return fadedColor
              } else if (latestDeliveryTime !== null && value > latestDeliveryTime) {
                return fadedColor
              } else {
                return nextColor
              }
            },
          },
          backgroundColor: nextColor,
        }
      }
      datasets[datasetName].data.push({ x: new Date(point.updateTime).getTime(), y: point.temperature })
      return datasets
    }, {}),
  )
}

export const getPickupAndDeliveryTimes = (legs: LegData[]): {
  earliestPickup: number | null,
  latestDelivery: number | null
} => {
  const pickupStatuses: StopStatus[] = []
  const deliveryStatuses: StopStatus[] = []
  legs.forEach(leg => {
    if (leg.wayPoints) {
      leg.wayPoints.forEach(waypoint => {
        if (waypoint.statuses.length > 0) {
          if (waypoint.type === 'PUP') {
            pickupStatuses.push(...waypoint.statuses)
          } else if (waypoint.type === 'DEL') {
            deliveryStatuses.push(...waypoint.statuses)
          }
        }
      })
    }
  })
  pickupStatuses.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  deliveryStatuses.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  const earliestPickup = pickupStatuses.length === 0 ? null : new Date(pickupStatuses[0]!.createdAt).getTime()
  const latestDelivery = deliveryStatuses.length === 0 ? null : new Date(deliveryStatuses[deliveryStatuses.length - 1]!.createdAt).getTime()
  return { earliestPickup, latestDelivery }
}

export const getTemperatureAnnotations = (temperatureRanges: PharmaExtendedInformation[], legs: LegData[]) => {
  const pickupAndDelTimes = getPickupAndDeliveryTimes(legs)
  const annotations: Annotation[] = []

  const addTemperatureRangeAnnotations = (value: PharmaExtendedInformation) => {
    if (value.minimum !== undefined && value.maximum !== undefined) {
      annotations.push({
        drawTime: 'beforeDraw',
        type: 'box',
        yMin: value.minimum,
        yMax: value.maximum,
        backgroundColor: alpha(theme.palette.primary.dark, 0.33),
        borderWidth: 0,
      })
      annotations.push({
        drawTime: 'beforeDraw',
        type: 'box',
        xMin: pickupAndDelTimes.earliestPickup ?? undefined,
        xMax: pickupAndDelTimes.latestDelivery ?? undefined,
        yMin: value.minimum,
        yMax: value.maximum,
        backgroundColor: alpha(theme.palette.primary.dark, 0.66),
        borderWidth: 0,
      })
    }
    if (value.setPoint !== undefined) {
      annotations.push({
        drawTime: 'beforeDraw',
        type: 'line',
        yMin: value.setPoint,
        yMax: value.setPoint,
        borderColor: alpha(theme.palette.secondary.main, 0.75),
        borderWidth: 1,
        borderDash: [2, 2],
      })
    }
  }

  const addWaypointAnnotations = (label: string, status: StopStatus) => {
    const createdAt = new Date(status.createdAt).getTime()
    annotations.push({
      drawTime: 'afterDatasetsDraw',
      type: 'line',
      xMin: createdAt,
      xMax: createdAt,
      borderColor: alpha(theme.palette.primary.light, 0.75),
      borderWidth: 1,
      borderDash: [6, 6],
      label: { content: label, display: false, position: 'end' },
      enter({ element }, event) {
        element.label.options.display = true
        return true
      },
      leave({ element }, event) {
        element.label.options.display = false
        return true
      },
    })
  }

  const findNearbyTimes = (statuses: LegStatus[], isWithinTime: (date1: string, date2: string) => boolean): void => {
    if (statuses.length > 1) {
      for (let i = 0; i < statuses.length; i++) {
        for (let j = i + 1; j < statuses.length; j++) {
          const label = isWithinTime(statuses[i]!.status.createdAt, statuses[j]!.status.createdAt) ?
            `${statuses[i]!.shipmentNumber} ${statuses[j]!.shipmentNumber}` : statuses[i]!.shipmentNumber
          addWaypointAnnotations(label, statuses[i]!.status)
        }
      }
    } else {
      const label = statuses[0]!.shipmentNumber
      addWaypointAnnotations(label, statuses[0]!.status)
    }
  }

  interface LegStatus {
    status: StopStatus
    shipmentNumber: string
  }

  const processStatuses = (waypoint: StopData, shipmentNumber: string, type: 'PUP' | 'DEL'): void => {
    if (waypoint.statuses && waypoint.statuses.length > 0) {
      waypoint.statuses.forEach(status => {
        const legStatus: LegStatus = { status: status, shipmentNumber: shipmentNumber }
        if (type === 'PUP') {
          pickupStatuses.push(legStatus)
        } else if (type === 'DEL') {
          deliveryStatuses.push(legStatus)
        }
      })
    }
  }

  const isWithinTwoHours = (date1: string, date2: string): boolean => {
    const diff = Math.abs(new Date(date1).getTime() - new Date(date2).getTime())
    return diff <= 2 * 60 * 60 * 1000 // 2 hours
  }

  let rangeMin: number | null = null
  let rangeMax: number | null = null
  temperatureRanges.forEach((range) => {
    if (range.minimum !== undefined && (rangeMin === null || rangeMin > range.minimum)) {
      rangeMin = range.minimum
    }
    if (range.maximum !== undefined && (rangeMax === null || rangeMax < range.maximum)) {
      rangeMax = range.maximum
    }
    addTemperatureRangeAnnotations(range)
  })

  const pickupStatuses: LegStatus[] = []
  const deliveryStatuses: LegStatus[] = []

  legs.forEach((leg) => {
    if (leg.wayPoints) {
      leg.wayPoints.forEach((waypoint) => {
        processStatuses(waypoint, leg.shipmentNumber, waypoint.type as 'PUP' | 'DEL')
      })
    }
  })

  if (pickupStatuses.length !== 0) {
    findNearbyTimes(pickupStatuses, isWithinTwoHours)
  }

  if (deliveryStatuses.length !== 0) {
    findNearbyTimes(deliveryStatuses, isWithinTwoHours)
  }

  return {
    plugins: {
      annotation: {
        annotations: annotations,
      },
    },
    scales: {
      y: {
        suggestedMin: rangeMin !== null ? rangeMin - 2 : null,
        suggestedMax: rangeMax !== null ? rangeMax + 2 : null,
      },
    },
  }
}

export const positionDataTransformer = (data: VehiclePositionMetricValue[]): GeoPoint[] => {
  const geoPoints = data.reduce((geoPoints: GeoPoint[], value: VehiclePositionMetricValue) => {
    const geoPoint: GeoPoint = {
      latitude: parseFloat(value.latitude),
      longitude: parseFloat(value.longitude),
      timestamp: value.updateTime,
      estimatedSpeed: 0,
    }
    if (geoPoints[geoPoints.length - 1]?.latitude === geoPoint.latitude && geoPoints[geoPoints.length - 1]?.longitude === geoPoint.longitude) {
      geoPoints[geoPoints.length - 1]!.lastTimestamp = geoPoint.timestamp
    } else {
      geoPoints.push(geoPoint)
    }
    return geoPoints
  }, [])
  // speed estimation
  let previousGeoPoint: GeoPoint
  geoPoints.forEach((geoPoint: GeoPoint) => {
    if (previousGeoPoint?.timestamp && geoPoint.timestamp) {
      geoPoint.distanceToPrevious = getDistance(geoPoint, previousGeoPoint)
      geoPoint.timeToPrevious = differenceInSeconds(parseISO(geoPoint.timestamp), parseISO(previousGeoPoint.lastTimestamp ?? previousGeoPoint.timestamp))
      // geopoints with more than 45 mins time difference will be marked as "interrupted" (speed = -1) and shown differently on a map
      geoPoint.estimatedSpeed = geoPoint.timeToPrevious <= 45 * 60 ? Math.ceil(geoPoint.distanceToPrevious * (60 * 60 / geoPoint.timeToPrevious)) : -1
    }
    previousGeoPoint = geoPoint
    return geoPoint
  })
  // heading calculation done on reversed list to more easily grab "next" geopoint
  let nextGeoPoint: GeoPoint
  geoPoints.reverse()
  geoPoints.forEach((geoPoint: GeoPoint) => {
    if (nextGeoPoint) {
      geoPoint.heading = getHeading(geoPoint, nextGeoPoint)
    }
    nextGeoPoint = geoPoint
    return geoPoint
  })
  // reverse again for proper order
  return geoPoints.reverse()
}

export const getEstimatedSpeedLabel = (estimatedSpeed?: number): string => {
  if (!estimatedSpeed || estimatedSpeed === -1) {
    return 'interruption'
  } else if (estimatedSpeed < 30) {
    return 'vehicle_slow'
  } else if (estimatedSpeed < 60) {
    return 'vehicle_medium'
  }
  return 'vehicle_fast'
}

export const getRouteColor = (label?: string): string => {
  switch (label) {
    case 'vehicle_slow':
      return '#fa5352'
    case 'vehicle_medium':
      return '#fab007'
    case 'vehicle_fast':
      return '#08d76b'
    case 'interruption':
      return '#fa5352'
    case 'shortest_route':
      return theme.palette.primary.main
    default:
      return theme.palette.primary.light
  }
}

export const groupGeoPointsBySpeed = (geoPoints: GeoPoint[]): GeoPointsGroup[] => {
  if (geoPoints.some((geoPoint) => geoPoint.estimatedSpeed == undefined)) {
    return [
      {
        label: 'unknown',
        geoPoints: geoPoints,
      },
    ]
  }
  return geoPoints.reduce((groups: GeoPointsGroup[], geoPoint) => {
    /* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
    const label = getEstimatedSpeedLabel(geoPoint.estimatedSpeed!)
    const previousGroup = groups[groups.length - 1]
    if (!previousGroup || previousGroup.label != label) {
      const newGroup: GeoPointsGroup = {
        label: label,
        geoPoints: [],
      }
      if (previousGroup) {
        // NOTE: include last geopoint of previous group as a starting point of new one
        newGroup.geoPoints.push(previousGroup.geoPoints[previousGroup.geoPoints.length - 1]!)
      }
      newGroup.geoPoints.push(geoPoint)
      groups.push(newGroup)
    } else {
      previousGroup.geoPoints.push(geoPoint)
    }
    return groups
  }, [])
}

export const getParkingLocationColor = (type: ParkingLocationType): string => {
  switch (type) {
    case ParkingLocationType.KUEHNE_NAGEL:
      return '#009adb'
    case ParkingLocationType.STRATEGIC:
      return '#845ef7'
    case ParkingLocationType.BASIC_SECURITY:
      return theme.palette.primary.light
    case ParkingLocationType.MEDIUM_SECURITY:
      return '#ff832b'
    case ParkingLocationType.HIGH_SECURITY:
      return '#08c792'
    case ParkingLocationType.NEGATIVE_PARKING:
      return '#da1e28'
    case ParkingLocationType.SHORT_BREAK_45MIN:
      return '#212529'
    default:
      return theme.palette.primary.light
  }
}

export const formatParkingLocationDescription = (text: string) =>
  text.trim()
    .replaceAll('(Barrier system)', 'Barrier system')
    .replaceAll(/^-/g, '')
    .replaceAll(/ -/g, '\n')
    .replaceAll(/(\w):([^ ]+)/g, '$1: $2')

export const getTypeLabel = (type: 'PUP' | 'DEL' | 'CUS'): string => {
  switch (type) {
    case 'PUP':
      return i18n.t('waypoints.types.PUP')
    case 'DEL':
      return i18n.t('waypoints.types.DEL')
    case 'CUS':
      return i18n.t('waypoints.types.CUS')
    default:
      return i18n.t('waypoints.types.UNKNOWN')
  }
}

export const getTypeIcon = (type: 'PUP' | 'DEL' | 'CUS'): any => {
  switch (type) {
    case 'PUP':
      return FileUploadIcon
    case 'DEL':
      return FileDownloadIcon
    case 'CUS':
      return KNCustomsIcon
    default:
      return QuestionMarkIcon
  }
}

export const getGroupedStopStatuses = (stopLegPairs: StopLegPair[]) => {
  const allStatuses = stopLegPairs.flatMap((stopLegPair) => stopLegPair.stop.statuses)
  const latestStatuses = new Map<string, StopStatus>()
  allStatuses.forEach((status) => {
    const existing = latestStatuses.get(status.id)
    if (!existing) {
      latestStatuses.set(status.id, status)
      return
    }
    if (subSeconds(parseISO(status.createdAt), status.createdAtOffset ?? 0) > subSeconds(parseISO(existing.createdAt), existing.createdAtOffset ?? 0)) {
      latestStatuses.set(status.id, status)
    }
  })
  return Array.from(latestStatuses.values())
}
